import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Alert,
  Snackbar
} from '@mui/material';
import { Icons } from '../../config/MuiIcons.config';
import sunriseIcon from '../../assets/images/Sunrise.png';
import sunsetIcon from '../../assets/images/Sunset.png';
import moonriseIcon from '../../assets/images/Moonrise.png';
import moonsetIcon from '../../assets/images/Moonset.png';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoIcon from '@mui/icons-material/Info';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import EventIcon from '@mui/icons-material/Event';
import { AppTheme } from '../../theme/App.theme';
import { useOptimizedGeolocation } from '../../hook/GeoLocation/useOptimizedGeolocationHook';
import hinduCalendarService from '../../services/HinduCalenderService';
import HorizontalCalendar from './components/HorizontalCalendar/HorizontalCalendar';
import EventCard from './components/EventCard/EventCard';
import PanchangSection from './components/PanchangSection/PanchangSection';
import PanchangAccordion from './components/PanchangAccordion/PanchangAccordion';
import InfoCard from './components/InfoCard/InfoCard';
import omSymbol from '../../assets/images/OmSymbol.png';
import './HinduCalendar.css';
import dayjs from 'dayjs';
import { AppGlobalContext } from '../../contexts/AppContext.context';
import MUILoader from '../../components/MUILoader/MUILoader';
import { languagesForHinduCalendar } from '../../config/App.config';

const HinduCalendar = () => {
  const { authUserDetails } = useContext(AppGlobalContext);
  // Location data
  const { city, latitude, longitude, timezone, isLocationAvailable } = useOptimizedGeolocation();
  
  // State management
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [panchangData, setPanchangData] = useState(null);
  const [monthEvents, setMonthEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  // Fetch Panchang data
  const fetchPanchangData = async (date) => {
    try {
      setLoading(true);
      setError(null);
      
      const requestData = {
        day: date.getDate(),
        month: date.getMonth() + 1, // JavaScript months are 0-based
        year: date.getFullYear(),
        place: city || 'Delhi', // Use city name or default to Delhi
        lat: latitude || 28.6139, // Default to Delhi coordinates if not available
        lon: longitude || 77.2090, // Default to Delhi coordinates if not available
        tzone: timezone || 'Asia/Kolkata',
        lan: languagesForHinduCalendar[authUserDetails?.preferredLanguage?.toLowerCase() || "english"],
      };

      const response = await hinduCalendarService.getPanchangData(requestData);
      setPanchangData(response?.data);
    } catch (err) {
      console.error('Error fetching Panchang data:', err);
      setError(err.message || 'Failed to fetch Panchang data');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Fetch month events based on the selected date
  const fetchMonthEvents = async (date = selectedDate) => {
    try {
      const requestData = {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        place: city || 'Delhi', // Use city name or default to Delhi
        lat: latitude || 28.6139, // Default to Delhi coordinates if not available
        lon: longitude || 77.2090, // Default to Delhi coordinates if not available
        tzone: timezone || 5.5, // Default to India's timezone
        lan: languagesForHinduCalendar[authUserDetails?.preferredLanguage?.toLowerCase() || "english"],
      };

      const response = await hinduCalendarService.getMonthSpecificEvents(requestData);
      setMonthEvents(response.data || []);
    } catch (err) {
      console.error('Error fetching month events:', err);
    }
  };

  // console.log("panchangData", panchangData);

  // Handle date selection
  const handleDateSelect = (date) => {
    const oldMonth = selectedDate.getMonth();
    const oldYear = selectedDate.getFullYear();
    const newMonth = date.getMonth();
    const newYear = date.getFullYear();
    
    setSelectedDate(date);
    fetchPanchangData(date);
    
    // Fetch month events only if month or year has changed
    if (oldMonth !== newMonth || oldYear !== newYear) {
      fetchMonthEvents(date);
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (!authUserDetails?.uid) return;

    if (authUserDetails?.uid && isLocationAvailable) {
      fetchPanchangData(selectedDate);
      fetchMonthEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUserDetails, isLocationAvailable, latitude, longitude, timezone]);

  // Process date-specific events from Panchang data
  const getDateSpecificEvents = () => {
    if (!panchangData?.dateSpecificEvents) return [];
    
    // Format selected date for comparison
    const selectedDateStr = dayjs(selectedDate).format('YYYY-MM-DD');
    
    // Filter events that match the selected date
    const filteredEvents = panchangData.dateSpecificEvents.filter(event => {
      if (!event.date) return false;
      
      // Format event date for comparison
      const eventDateStr = dayjs(event.date).format('YYYY-MM-DD');
      
      // Check if event date matches selected date
      return eventDateStr === selectedDateStr;
    });
    
    return filteredEvents.map(event => ({
      title: event.eventName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      date: event.date ? dayjs(event.date).format('MMM DD YYYY') : "Date Not Available",
      image: event.image || null,
      type: 'Festival',
    }));
  };

  // Process upcoming events from month data with date filtering
  const getUpcomingEvents = () => {
    if (!monthEvents || monthEvents.length === 0) return [];
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    
    const selectedDateObj = new Date(selectedDate);
    selectedDateObj.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    
    // Determine the reference date for filtering
    let referenceDate;
    if (selectedDateObj < today) {
      // Scenario 1: User selected a past date - show events from current day onward
      referenceDate = today;
    } else {
      // Scenario 2: User selected a future date - show events from that selected date onward
      referenceDate = selectedDateObj;
    }
    
    // Filter events based on the reference date
    const filteredEvents = monthEvents.filter(event => {
      if (!event.date) return false;
      
      const eventDate = new Date(event.date);
      eventDate.setHours(0, 0, 0, 0); // Reset time to start of day
      
      // Include events that are on or after the reference date
      return eventDate >= referenceDate;
    });
    
    return filteredEvents.map(event => ({
      title: event.eventName?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      date: event.date ? dayjs(event.date).format('MMM DD YYYY') : "Date Not Available",
      image: event.image || null,
      type: 'Event',
    }));
  };

  // get only date of upcoming events
  const getOnlyDateOfUpcomingEvents = () => {
    return monthEvents.map(event => event.date ? dayjs(event.date).format('MMM DD YYYY') : "");
  };

  // Get all event dates (both date-specific and upcoming events)
  const getAllEventDates = () => {
    const dateSpecificDates = panchangData?.dateSpecificEvents 
      ? panchangData.dateSpecificEvents.map(event => event.date ? dayjs(event.date).format('MMM DD YYYY') : "").filter(date => date !== "")
      : [];
    const upcomingDates = getOnlyDateOfUpcomingEvents().filter(date => date !== "");
    return [...new Set([...dateSpecificDates, ...upcomingDates])]; // Remove duplicates
  };
  
  // Process auspicious timings
  const getAuspiciousTimings = () => {
    if (!panchangData?.auspiciousTimings) return [];
    
    const timings = [];
    const data = panchangData.auspiciousTimings;
    
    // Process all timing objects
    Object.entries(data).forEach(([key, timing]) => {
      if (timing && timing.startTime && timing.endTime && timing.startTime !== "" && timing.endTime !== "") {
        // Format time from "2025-10-10 04:49:31" to "04:49 AM"
        const formatTime = (timeStr) => {
          const date = new Date(timeStr);
          return date.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
          });
        };
        
        timings.push({
          title: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
          value: `${formatTime(timing.startTime)} - ${formatTime(timing.endTime)}`,
        });
      }
    });
    
    return timings;
  };

  // Process inauspicious timings
  const getInauspiciousTimings = () => {
    if (!panchangData?.inauspiciousPeriods) return [];
    
    const timings = [];
    const data = panchangData.inauspiciousPeriods;
    
    // Format time helper function
    const formatTime = (timeStr) => {
      if (!timeStr) return "";
      const date = new Date(timeStr);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    };
    
    // Process timing data (handles both object and array)
    const processTiming = (key, timingData) => {
      if (!timingData) return;
      
      const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
      
      // Check if it's an array
      if (Array.isArray(timingData)) {
        if (timingData.length > 0) {
          timingData.forEach((timing, index) => {
            if (timing && timing.startTime && timing.endTime) {
              const title = timingData.length > 1 
                ? `${formattedKey} ${index + 1}`
                : formattedKey;
              
              timings.push({
                title,
                value: `${formatTime(timing.startTime)} - ${formatTime(timing.endTime)}`,
              });
            }
          });
        }
      } 
      // Check if it's an object with startTime and endTime
      else if (timingData && typeof timingData === 'object' && timingData.startTime && timingData.endTime) {
        timings.push({
          title: formattedKey,
          value: `${formatTime(timingData.startTime)} - ${formatTime(timingData.endTime)}`,
        });
      }
    };
    
    // Process all inauspicious periods (handles both object and array data)
    processTiming('Rahu Kaal', data.rahuKaal);
    processTiming('Gul Kai Kaal', data.gulkaiKaal);
    processTiming('Yamaganda', data.yamaganda);
    processTiming('Varjyam', data.varjyam);
    processTiming('Vidaal Yoga', data.vidaalYoga);
    processTiming('Baana', data.baana);
    processTiming('Dur Muhurtam', data.durMuhurtam);
    processTiming('Panchaka', data.panchaka);
    processTiming('Hutashana Yoga', data.hutashanaYoga);
    processTiming('Visha Yoga', data.vishaYoga);
    processTiming('Yamaghata Yoga', data.yamaghataYoga);
    processTiming('Dagdha Yoga', data.dagdhaYoga);
    processTiming('Samvartaka Yoga', data.samvartakaYoga);
    processTiming('Kakracha Yoga', data.kakrachaYoga);
    processTiming('Mrityu Yoga', data.mrityuYoga);
    processTiming('Vidaal Yoga Detailed', data.vidaalYogaDetailed);
    processTiming('Aadal Yoga', data.aadalYoga);
    processTiming('Aadal Yoga Detailed', data.aadalYogaDetailed);
    
    return timings;
  };

  // Process celestial timings
  const getCelestialTimings = () => {
    if (!panchangData?.sunMoon) return [];
    
    const timings = [];
    const data = panchangData.sunMoon;
    
    if (data.sunRise) timings.push({ title: 'Sunrise', value: dayjs(data.sunRise, "hh:mm:ss A").format("hh:mm A"), icon: sunriseIcon });
    if (data.sunSet) timings.push({ title: 'Sunset', value: dayjs(data.sunSet, "hh:mm:ss A").format("hh:mm A"), icon: sunsetIcon });
    if (data.moonRise) timings.push({ title: 'Moonrise', value: dayjs(data.moonRise, "hh:mm:ss A").format("hh:mm A"), icon: moonriseIcon });
    if (data.moonSet) timings.push({ title: 'Moonset', value: dayjs(data.moonSet, "hh:mm:ss A").format("hh:mm A"), icon: moonsetIcon });
    
    return timings;
  };

  // Process epoch information
  const getEpochInformation = () => {
    if (!panchangData?.epochDetails) return [];
    
    const epoch = [];
    const data = panchangData.epochDetails;
    
    if (data.kaliYuga) epoch.push({ title: 'Kaliyuga', value: data.kaliYuga });
    if (data.lahiriAyanamsha) epoch.push({ title: 'Lahiri Ayanamsha', value: data.lahiriAyanamsha });
    // if (data.julianDay) epoch.push({ title: 'Julian Day', value: data.julianDay });
    // if (data.modifiedJulianDay) epoch.push({ title: 'Modified Julian Day', value: data.modifiedJulianDay });
    // if (data.julianDate) epoch.push({ title: 'Julian Date', value: data.julianDate });
    // if (data.rataDie) epoch.push({ title: 'Rata Die', value: data.rataDie });
    if (data.nationalCivilDate) epoch.push({ title: 'National Civil Date', value: data.nationalCivilDate });
    if (data.kaliAhargana) epoch.push({ title: 'Kali Ahargana', value: data.kaliAhargana });
    
    return epoch;
  };

  // Helper function to check if a value is empty
  const isEmpty = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === 'object' && Object.keys(value).length === 0) return true;
    return false;
  };

  // Helper function to filter empty values from an object
  const filterEmptyValues = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    
    const filtered = {};
    Object.keys(obj).forEach(key => {
      if (!isEmpty(obj[key])) {
        filtered[key] = obj[key];
      }
    });
    return filtered;
  };

  // Check if Panchanga data exists
  const hasPanchangData = () => {
    if (!panchangData?.dailyPanchang) return false;
    
    const hasTithis = panchangData.dailyPanchang?.tithis && panchangData.dailyPanchang.tithis.length > 0;
    const hasYogas = panchangData.dailyPanchang?.yogas && panchangData.dailyPanchang.yogas.length > 0;
    const hasKarnas = panchangData.dailyPanchang?.karnas && panchangData.dailyPanchang.karnas.length > 0;
    const hasNakshatras = panchangData.dailyPanchang?.nakshatras?.nakshatraPada && panchangData.dailyPanchang.nakshatras.nakshatraPada.length > 0;
    const hasSunNakshatras = panchangData.dailyPanchang?.sunNakshatras?.nakshatraPada && panchangData.dailyPanchang.sunNakshatras.nakshatraPada.length > 0;
    const hasWeekDay = panchangData.summary?.weekDay;
    
    return hasTithis || hasYogas || hasKarnas || hasNakshatras || hasSunNakshatras || hasWeekDay;
  };

  // Check if any data is available to display
  const hasAnyDataToDisplay = () => {
    return (
      getDateSpecificEvents().length > 0 ||
      hasPanchangData() ||
      getAuspiciousTimings().length > 0 ||
      getInauspiciousTimings().length > 0 ||
      getCelestialTimings().length > 0 ||
      getEpochInformation().length > 0 ||
      getUpcomingEvents().length > 0
    );
  };

  return (
    <Box className="hindu-calendar-container">
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          marginBottom: "10px",
        }}
      >
        <img
          src={omSymbol}
          alt="Om Symbol"
          style={{
            width: "50px",
            height: "50px",
            marginRight: "12px",
          }}
        />
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: "28px", sm: "36px", md: "44px" },
            fontWeight: 700,
            color: AppTheme.PRIMARY_COLOR,
            textAlign: "center",
          }}
        >
          Digital Panchanga
        </Typography>
      </Box>

      <Container maxWidth="xl" className='px-1'>
        {/* Calendar */}
        <Box sx={{ marginBottom: { xs: "24px", sm: "32px" } }}>
          <HorizontalCalendar
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            eventDates={getAllEventDates()}
          />
        </Box>

        {/* Loading State */}
        {loading && <MUILoader IsMiniLoader />}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ marginBottom: "24px" }}>
            {error}
          </Alert>
        )}

        {/* Main Content */}
        {panchangData && !loading && (
          <>
            {/* No Data Available Message */}
            {!hasAnyDataToDisplay() && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "40px 20px",
                  backgroundColor: "#F9FAFB",
                  borderRadius: "12px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                }}
              >
                <InfoIcon
                  sx={{
                    fontSize: { xs: "48px", sm: "60px" },
                    color: "#9CA3AF",
                    marginBottom: "16px",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: { xs: "18px", sm: "22px" },
                    fontWeight: 600,
                    color: "#374151",
                    marginBottom: "8px",
                    textAlign: "center",
                  }}
                >
                  No Panchanga Data Available
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "14px", sm: "16px" },
                    color: "#6B7280",
                    textAlign: "center",
                    maxWidth: "500px",
                  }}
                >
                  We couldn't retrieve Panchanga information for the selected
                  date. Please try selecting a different date.
                </Typography>
              </Box>
            )}

            {/* Data Sections */}
            {hasAnyDataToDisplay() && (
              <Grid container spacing={2}>
                <Grid size={12}>
                  {/* Date Specific Events */}
                  {getDateSpecificEvents().length > 0 && (
                    <Box sx={{ marginBottom: { xs: "24px", sm: "32px" } }}>
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 600,
                          marginBottom: "10px",
                          color: AppTheme.PRIMARY_BLACK,
                        }}
                      >
                        Date Specific Events
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          overflowX: "auto",
                          gap: { xs: "8px", sm: "12px" },
                          msOverflowStyle: "none", // Hide scrollbar for IE/Edge
                          scrollbarWidth: "none", // Hide scrollbar for Firefox
                          "&::-webkit-scrollbar": {
                            display: "none", // Hide scrollbar for Chrome/Safari/Opera
                          },
                        }}
                      >
                        {getDateSpecificEvents().map((event, index) => (
                          <EventCard
                            key={index}
                            event={event}
                            variant="default"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Grid>

                {/* Panchang Section */}
                {hasPanchangData() && (
                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                    <PanchangSection
                      title="Panchanga"
                      icon={<AccessTimeIcon />}
                      textColor="white"
                      headerColor="#1E3D59"
                    >
                      <Grid container spacing={2}>
                        {panchangData.dailyPanchang?.tithis &&
                          panchangData.dailyPanchang.tithis.length > 0 && (
                            <Grid size={12}>
                              <PanchangAccordion
                                title="Tithi"
                                data={panchangData.dailyPanchang.tithis.map(
                                  (tithi) => filterEmptyValues({
                                    name: tithi.tithi,
                                    number: tithi.number,
                                    type: tithi.type,
                                    paksha: tithi.paksha,
                                    deity: tithi.deity,
                                    vriddhi: tithi.vriddhi,
                                    kshaya: tithi.kshaya,
                                    startTime: tithi.startTime,
                                    endTime: tithi.endTime,
                                    startTimeFormatted: dayjs(
                                      tithi.startTime
                                    ).format("MMM DD YYYY, hh:mm A"),
                                    endTimeFormatted: dayjs(
                                      tithi.endTime
                                    ).format("MMM DD YYYY, hh:mm A"),
                                    gandanta: tithi.gandanta,
                                    pakshaRandhra: tithi.pakshaRandhra,
                                  })
                                )}
                                type="tithi"
                              />
                            </Grid>
                          )}
                        {panchangData.dailyPanchang?.yogas &&
                          panchangData.dailyPanchang.yogas.length > 0 && (
                            <Grid size={12}>
                              <PanchangAccordion
                                title="Yoga"
                                data={panchangData.dailyPanchang.yogas.map(
                                  (yoga) => filterEmptyValues({
                                    name: yoga.yogaName,
                                    number: yoga.yogaNumber,
                                    startTime: yoga.startTime,
                                    endTime: yoga.endTime,
                                    startTimeFormatted: dayjs(
                                      yoga.startTime
                                    ).format("MMM DD YYYY, hh:mm A"),
                                    endTimeFormatted: dayjs(
                                      yoga.endTime
                                    ).format("MMM DD YYYY, hh:mm A"),
                                    vishaGhatis: yoga.vishaGhatis,
                                  })
                                )}
                                type="yoga"
                              />
                            </Grid>
                          )}
                        {panchangData.dailyPanchang?.karnas &&
                          panchangData.dailyPanchang.karnas.length > 0 && (
                            <Grid size={12}>
                              <PanchangAccordion
                                title="Karana"
                                data={panchangData.dailyPanchang.karnas.map(
                                  (karana) => filterEmptyValues({
                                    name: karana.karanaName,
                                    devata: karana.devata,
                                    nature: karana.nature,
                                    paksha: karana.paksha,
                                    rulingPlanet: karana.rullingPlanet,
                                    tithi: karana.tithi,
                                    mobility: karana.mobility,
                                    startTime: karana.startTime,
                                    endTime: karana.endTime,
                                    startTimeFormatted: dayjs(
                                      karana.startTime
                                    ).format("MMM DD YYYY, hh:mm A"),
                                    endTimeFormatted: dayjs(
                                      karana.endTime
                                    ).format("MMM DD YYYY, hh:mm A"),
                                    bhadra: karana.bhadra,
                                    bhadraNiwas: karana.bhadraNiwas,
                                  })
                                )}
                                type="karana"
                              />
                            </Grid>
                          )}
                        {panchangData.dailyPanchang?.nakshatras?.nakshatraPada &&
                          panchangData.dailyPanchang.nakshatras.nakshatraPada.length > 0 && (
                            <Grid size={12}>
                              <PanchangAccordion
                                title="Nakshatra"
                                data={panchangData.dailyPanchang.nakshatras.nakshatraPada.map(
                                  (nakshatra) => filterEmptyValues({
                                    name: nakshatra.nakName,
                                    lord: nakshatra.lord,
                                    deity: nakshatra.deity,
                                    element: nakshatra.element,
                                    symbol: nakshatra.symbol,
                                    syllables: nakshatra.syllables,
                                    pursuit: nakshatra.pursuit,
                                    gana: nakshatra.gana,
                                    direction: nakshatra.direction,
                                    guna: nakshatra.guna,
                                    bodyPart: nakshatra.bodyPart,
                                    color: nakshatra.color,
                                    nakNumber: nakshatra.nakNumber,
                                    nakPada: nakshatra.nakPada,
                                    startTime: nakshatra.startTime || "",
                                    endTime: nakshatra.endTime || "",
                                    startTimeFormatted: nakshatra.startTime ? dayjs(
                                      nakshatra.startTime
                                    ).format("MMM DD YYYY, hh:mm A") : "",
                                    endTimeFormatted: nakshatra.endTime ? dayjs(
                                      nakshatra.endTime
                                    ).format("MMM DD YYYY, hh:mm A") : "",
                                  })
                                )}
                                type="nakshatra"
                              />
                            </Grid>
                          )}
                        {panchangData.dailyPanchang?.sunNakshatras?.nakshatraPada &&
                          panchangData.dailyPanchang.sunNakshatras.nakshatraPada.length > 0 && (
                            <Grid size={12}>
                              <PanchangAccordion
                                title="Sun Nakshatra"
                                data={panchangData.dailyPanchang.sunNakshatras.nakshatraPada.map(
                                  (nakshatra) => filterEmptyValues({
                                    name: nakshatra.nakName,
                                    lord: nakshatra.lord,
                                    deity: nakshatra.deity,
                                    element: nakshatra.element,
                                    symbol: nakshatra.symbol,
                                    syllables: nakshatra.syllables,
                                    pursuit: nakshatra.pursuit,
                                    gana: nakshatra.gana,
                                    direction: nakshatra.direction,
                                    guna: nakshatra.guna,
                                    bodyPart: nakshatra.bodyPart,
                                    color: nakshatra.color,
                                    nakNumber: nakshatra.nakNumber,
                                    nakPada: nakshatra.nakPada,
                                    startTime: nakshatra.startTime || "",
                                    endTime: nakshatra.endTime || "",
                                    startTimeFormatted: nakshatra.startTime ? dayjs(
                                      nakshatra.startTime
                                    ).format("MMM DD YYYY, hh:mm A") : "",
                                    endTimeFormatted: nakshatra.endTime ? dayjs(
                                      nakshatra.endTime
                                    ).format("MMM DD YYYY, hh:mm A") : "",
                                  })
                                )}
                                type="nakshatra"
                              />
                            </Grid>
                          )}
                        {panchangData.summary?.weekDay && (
                          <Grid size={12}>
                            <PanchangAccordion
                              title="Var (Weekday)"
                              data={[
                                {
                                  name: panchangData.summary.weekDay,
                                },
                              ]}
                              type="var"
                            />
                          </Grid>
                        )}
                      </Grid>
                    </PanchangSection>
                  </Grid>
                )}

                {/* Auspicious Timings */}
                {getAuspiciousTimings().length > 0 && (
                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                    <PanchangSection
                      title="Auspicious Timings"
                      icon={<AccessTimeIcon />}
                      headerColor="#18BAA6"
                      textColor="white"
                    >
                      <Grid container spacing={2}>
                        {getAuspiciousTimings().map((timing, index) => (
                          <Grid size={12} key={index}>
                            <InfoCard
                              title={timing.title}
                              value={timing.value}
                              variant="auspicious"
                              timingColor="#1E3D59"
                              titleColor="#1E3D59"
                              backgroundColor="#EFF6FF"
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </PanchangSection>
                  </Grid>
                )}

                {/* Inauspicious Timings */}
                {getInauspiciousTimings().length > 0 && (
                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                    <PanchangSection
                      title="Inauspicious Periods"
                      icon={<Icons.ErrorOutlineIcon />}
                      headerColor="#FC4613"
                      textColor="white"
                    >
                      <Grid container spacing={2}>
                        {getInauspiciousTimings().map((timing, index) => (
                          <Grid size={12} key={index}>
                            <InfoCard
                              title={timing.title}
                              value={timing.value}
                              variant="inauspicious"
                              timingColor="#E73C08"
                              titleColor="#E73C08"
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </PanchangSection>
                  </Grid>
                )}

                {/* Celestial Timings */}
                {getCelestialTimings().length > 0 && (
                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                    <PanchangSection
                      title="Celestial Timings"
                      icon={<WbSunnyIcon />}
                      headerColor="#0369A1"
                      textColor="white"
                    >
                      <Grid container spacing={2}>
                        {getCelestialTimings().map((timing, index) => (
                          <Grid size={6} key={index}>
                            <InfoCard
                              title={timing.title}
                              value={timing.value}
                              icon={timing.icon}
                              variant="celestial"
                              timingColor="#0369A1"
                              titleColor="#0369A1"
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </PanchangSection>
                  </Grid>
                )}

                {/* Epoch Information */}
                {getEpochInformation().length > 0 && (
                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                    <PanchangSection
                      title="Epoch Information"
                      icon={<InfoIcon />}
                      headerColor="#5D7ED0"
                      textColor="white"
                    >
                      <Grid container spacing={2}>
                        {getEpochInformation().map((info, index) => (
                          <Grid size={12} key={index}>
                            <InfoCard
                              title={info.title}
                              value={info.value}
                              variant="epoch"
                              timingColor="#5D7ED0"
                              titleColor="#5D7ED0"
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </PanchangSection>
                  </Grid>
                )}

                {/* Upcoming Events */}
                {getUpcomingEvents().length > 0 && (
                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                    <PanchangSection
                      title="Upcoming Events"
                      icon={<EventIcon />}
                      headerColor="#ff9800"
                      textColor="white"
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gap: "16px",
                          padding: "8px 0",
                        }}
                      >
                        {getUpcomingEvents().map((event, index) => (
                          <EventCard
                            key={index}
                            event={event}
                            variant="default"
                          />
                        ))}
                      </Box>
                    </PanchangSection>
                  </Grid>
                )}
              </Grid>
            )}
          </>
        )}
      </Container>

      {/* Snackbar for errors */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HinduCalendar;
